import { GazeData, PauseSnapshot } from '../types';

/**
 * PauseTracker — measures per-modality "time since last activity".
 *
 * Why pauses are a 1st-class signal:
 *   Inattention has no positive action to detect — you can only see it as the
 *   *absence* of activity. The same pause length carries different meanings
 *   depending on which modalities are paused and what state the user is in:
 *     - 모든 채널 정지 + 시선도 고정 → Inattention
 *     - 키보드/스타일러스 정지 + 시선은 움직임 (THINKING_UP) → 고민중
 *     - 키보드 정지 + 시선이 LOOKING_SIDE/DOWN → 작성 정지 중 시선 이동
 *
 * Keyboard/stylus already track lastInputTime in their hooks. PauseTracker adds
 * gaze and voice tracking and then assembles a unified PauseSnapshot.
 */
export class PauseTracker {
  /** Threshold in degrees: gaze movement below this counts as "no motion". */
  private readonly gazeMotionThreshDeg: number;

  private lastGazeMoveT: number;
  private lastVoiceT: number;
  private lastYaw: number | null = null;
  private lastPitch: number | null = null;

  constructor(gazeMotionThreshDeg = 1.5) {
    this.gazeMotionThreshDeg = gazeMotionThreshDeg;
    const now = Date.now();
    this.lastGazeMoveT = now;
    this.lastVoiceT = now;
  }

  /** Reset all timers — call when monitoring (re)starts. */
  reset(now: number = Date.now()) {
    this.lastGazeMoveT = now;
    this.lastVoiceT = now;
    this.lastYaw = null;
    this.lastPitch = null;
  }

  /**
   * Feed a gaze sample. If yaw/pitch moved more than the threshold since the
   * last "motion" sample, mark this as the new motion timestamp.
   */
  updateGaze(gaze: GazeData, now: number = Date.now()) {
    if (this.lastYaw == null || this.lastPitch == null) {
      this.lastYaw = gaze.yaw;
      this.lastPitch = gaze.pitch;
      this.lastGazeMoveT = now;
      return;
    }
    const dy = gaze.yaw - this.lastYaw;
    const dp = gaze.pitch - this.lastPitch;
    const dist = Math.sqrt(dy * dy + dp * dp);
    if (dist >= this.gazeMotionThreshDeg) {
      this.lastGazeMoveT = now;
      this.lastYaw = gaze.yaw;
      this.lastPitch = gaze.pitch;
    }
  }

  /** Feed voice activity — call once per loop with current speaking state. */
  updateVoice(isSpeaking: boolean, now: number = Date.now()) {
    if (isSpeaking) this.lastVoiceT = now;
  }

  /**
   * Build a PauseSnapshot. Pass the current keyboard/stylus lastInputTime
   * values (already tracked by their hooks) — `0` means "no activity yet",
   * which we treat as a very long pause (Number.MAX_SAFE_INTEGER) so rules
   * with `minMs` still trigger when nothing has happened.
   */
  snapshot(now: number, lastKeyT: number, lastStylusT: number): PauseSnapshot {
    const gazeMs = now - this.lastGazeMoveT;
    const voiceMs = now - this.lastVoiceT;
    const keyboardMs = lastKeyT > 0 ? now - lastKeyT : Number.MAX_SAFE_INTEGER;
    const stylusMs = lastStylusT > 0 ? now - lastStylusT : Number.MAX_SAFE_INTEGER;

    // multimodal = "time since most recent activity in ANY channel"
    const multimodalMs = Math.min(gazeMs, voiceMs, keyboardMs, stylusMs);

    return { gazeMs, keyboardMs, stylusMs, voiceMs, multimodalMs };
  }
}
